/* eslint-disable camelcase */
import { useState, useMemo, useEffect } from 'react';
import cn from 'classnames';
import { useQuery } from 'react-query';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoBlue from '../assets/img/logo-blue.png';
import { NS } from '../../../models/I18n';
import { Type, EligibilitySubmission } from '../../../models/UserServerData';
import { Option } from '../../../models/Option';
import Education from '../../../models/Education';
import { LanguageTest } from '../../../models/LanguageTest';
import SpecificAnswers from '../../../models/SpecificAnswers';
import Button from '../../../shared/components/buttons/Button';
import Workflow from './Workflow';
import { ReactComponent as BackImg } from '../../../form/assets/back.svg';
import { ReactComponent as NextImg } from '../../../form/assets/next.svg';
import * as apiClient from '../../apiClient';
import { Spouse } from '../../../models/Spouse';
import LanguageSwitcher from '../../../shared/components/localization/LanguageSwitcher';
import { Organization } from '../../../models/PartnerServerData';
import { Employment } from '../../../models/Employment';
import { Job } from '../../../models/Job';
import countriesEN from '../../../shared/data/countries-en.json';
import { Lead } from '../../../models/Lead';
import { Country } from '../../../models/Country';
import { useRouterParamQuery } from '../../../shared/utils/useRouterParamQuery';
import { GeneralCheckReminder } from '../../../form/components/GeneralCheckReminder';
import { Img } from '../../../shared/components/img/Img';
import { CompletionPercentage } from '../../../shared/components/CompletionPercentage';

function Eligibility() {
  const routerParamQuery = useRouterParamQuery();
  const selectedCountryFromParam = (routerParamQuery.get('country') as Country) || null;
  const selectedTypeFromParam = (routerParamQuery.get('type') as Type) || '';

  const [questionLength, setQuestionLength] = useState(15);
  const { t } = useTranslation([NS.Eligibility, NS.Shared]);
  const history = useHistory();
  const match = useRouteMatch<{ eligibilityCheckToken: string }>('/eligibility/:eligibilityCheckToken');
  const accessToken = match?.params.eligibilityCheckToken;
  // country selection is only available for default lead gen
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(match ? 'CA' : selectedCountryFromParam);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [type, setType] = useState<Type | ''>(selectedTypeFromParam);
  const [failedGeneralEligibility, setFailedGeneralEligibility] = useState<boolean | undefined>(undefined);

  const [citizenship, setCitizenship] = useState<Option | null>(null);
  const [residence, setResidence] = useState<Option | null>(null);

  const [education, setEducation] = useState<Education | null>(null);
  const [isEducationInCanada, setIsEducationInCanada] = useState<boolean | undefined>(undefined);
  const [isEducationInUS, setIsEducationInUS] = useState<boolean | undefined>(undefined);

  const [englishSkill, setEnglishSkill] = useState<LanguageTest | undefined | null>(undefined);
  const [frenchSkill, setFrenchSkill] = useState<LanguageTest | undefined | null>(undefined);

  const [jobInCanada, setJobInCanada] = useState<Job | undefined | null>(undefined);
  const [jobInUS, setJobInUS] = useState<Job | undefined | null>(undefined);

  const [employment, setEmployment] = useState<Employment | undefined | null>(undefined);
  const [employmentInUS, setEmploymentInUS] = useState<Employment | undefined | null>(undefined);

  const [netWorth, setNetWorth] = useState<number | null>(null);
  const [investable, setInvestable] = useState<number | null>(null);
  const [age, setAge] = useState<number | undefined>(undefined);
  const [specificAnswers, setSpecificAnswers] = useState<SpecificAnswers | undefined>(undefined);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [spouse, setSpouse] = useState<Spouse | undefined | null>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const setFormType = (newType: Type | '') => {
    setType(newType);
    if (!match) {
      switch (newType) {
        case 'visitor':
          setQuestionLength(5);
          break;
        case 'study':
          setQuestionLength(selectedCountry === 'CA' ? 7 : 6);
          break;
        case 'work':
          setQuestionLength(selectedCountry === 'CA' ? 8 : 9);
          break;
        case 'immigrate':
          setQuestionLength(selectedCountry === 'CA' ? 11 : 10);
          break;
        default:
          break;
      }
    }
    window.mixpanel.time_event('Eligibility_Check_Submitted');
  };

  // this is only needed when the page is first loaded
  useEffect(() => {
    setFormType(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useOrganizationQuery = () => {
    return useQuery<Organization, Error>(
      ['organization', accessToken],
      async () => {
        const res = await apiClient.getOrganizationByAccessToken(accessToken);
        const json = await res.json();
        return json;
      },
      { enabled: !!accessToken },
    );
  };
  const { data: organization } = useOrganizationQuery();

  const citizenshipInEnglish = useMemo(
    () => (citizenship ? countriesEN.find((country) => country.Code === citizenship.value) : undefined),
    [citizenship],
  );
  const residenceInEnglish = useMemo(
    () => (residence ? countriesEN.find((country) => country.Code === residence.value) : undefined),
    [residence],
  );

  const nextQuestion = () => {
    if (currentQuestion === 0) {
      window.gtag?.('event', 'start_eligibility_check', {
        event_category: 'engagement',
        event_label: 'eligibility',
        value: currentQuestion,
      });
    } else {
      window.gtag?.('event', 'next_eligibility_question', {
        event_category: 'engagement',
        event_label: 'eligibility',
        value: currentQuestion,
      });
    }
    setCurrentQuestion((localCurrentQuestion) => localCurrentQuestion + 1);
  };

  const previousQuestion = () => {
    setCurrentQuestion((localCurrentQuestion) => localCurrentQuestion - 1);
  };

  // when submitting from default external check, leads can submit without lead info
  const submitForm = async (lead: Lead | null) => {
    setIsLoading(true);
    window.gtag?.('event', 'eligibility_submitted', {
      event_category: 'engagement',
      event_label: 'eligibility',
    });
    window.fbq('track', 'InitiateCheckout', { content_ids: ['FB_Ads_Eligibility_Submission'] });
    const res = await apiClient.createEligibilitySubmissionFromLeadGenPortal({
      country: selectedCountry,
      programType: match ? 'all' : type, // Perform "all" for any other organizations except for SuperVisas
      leadIntention: type,
      citizenship: citizenshipInEnglish
        ? { label: citizenshipInEnglish.Name, value: citizenshipInEnglish.Code }
        : citizenship,
      residence: residenceInEnglish ? { label: residenceInEnglish.Name, value: residenceInEnglish.Code } : residence,
      education,
      isEducationInCanada,
      isEducationInUS,
      jobInCanada: jobInCanada || null,
      jobInUS: jobInUS || null,
      employment: employment || null,
      employmentInUS: employmentInUS || null,
      netWorth,
      investableCash: investable,
      age: age || null,
      failedGeneralEligibility,
      specificAnswers,
      leadInfo: lead,
      accessToken: accessToken || process.env.DEFAULT_ELIGIBILITY_CHECK_TOKEN,
      spouse: spouse || null,
      englishSkill: englishSkill || null,
      frenchSkill: frenchSkill || null,
    });

    const { submission_token }: EligibilitySubmission = await res.json();
    setIsLoading(false);
    history.push(`/eligibility/${submission_token}/result`);
  };

  // scroll to top when question changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentQuestion]);

  // when this page is redirected from one specific program in the landing page,
  // autofill country and program type for users, then go to the next question.
  useEffect(() => {
    if (selectedCountryFromParam && selectedTypeFromParam) {
      nextQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryFromParam, selectedTypeFromParam]);

  const showOrganizationLogo = () => {
    if (organization) {
      return (
        <img src={organization?.logo} alt={`${organization?.name}'s logo`} className="max-w-[80px] lg:max-w-[130px]" />
      );
    }
    return <div className="h-[30px] w-[80px] animate-pulse rounded-lg bg-gray-200 lg:h-[50px] lg:w-[130px]" />;
  };

  return (
    <>
      <h1 className="sr-only">SuperVisas | {t(`${NS.Shared}:home.visa-immigration-made-simple`)}</h1>
      <div className="flex w-full items-center justify-between px-3 pt-3">
        {match ? (
          showOrganizationLogo()
        ) : (
          <a href="/">
            <Img src={logoBlue} alt="SuperVisas' logo" className="max-w-[80px] lg:max-w-[130px]" />
          </a>
        )}
        <span className="flex items-center gap-x-4 lg:gap-x-6">
          <CompletionPercentage questionLength={questionLength} currentQuestion={currentQuestion} />
          <LanguageSwitcher showSpinner={false} />
        </span>
      </div>
      <div className="prose prose-slate mt-6 min-h-[80vh] px-4 pb-20 lg:mx-auto lg:mt-[60px] lg:max-w-[700px]">
        <div className="">
          <div className={currentQuestion > 0 ? 'block' : 'invisible'}>
            <Button
              type="button"
              variant="text"
              className="-ml-3 text-brand-blue hover:bg-transparent"
              onClick={previousQuestion}
            >
              <BackImg className="inline-block pb-[3px]" />
              <span className="ml-1.5">{`${t(`${NS.Eligibility}:eligibleProgramCards.back`)}`}</span>
            </Button>
          </div>
          <Workflow
            currentQuestion={currentQuestion}
            education={education}
            isEducationInCanada={isEducationInCanada}
            setIsCompleted={setIsCompleted}
            setEducation={setEducation}
            setIsEducationInCanada={setIsEducationInCanada}
            englishSkill={englishSkill}
            setEnglishSkill={setEnglishSkill}
            frenchSkill={frenchSkill}
            setFrenchSkill={setFrenchSkill}
            jobInCanada={jobInCanada}
            jobInUS={jobInUS}
            setJobInCanada={setJobInCanada}
            setJobInUS={setJobInUS}
            employment={employment}
            setEmployment={setEmployment}
            employmentInUS={employmentInUS}
            setEmploymentInUS={setEmploymentInUS}
            netWorth={netWorth}
            investable={investable}
            setNetWorth={setNetWorth}
            setInvestable={setInvestable}
            age={age}
            setAge={setAge}
            specificAnswers={specificAnswers}
            setSpecificAnswers={setSpecificAnswers}
            type={type}
            residence={residence}
            failedGeneralEligibility={failedGeneralEligibility}
            setFailedGeneralEligibility={setFailedGeneralEligibility}
            citizenship={citizenship}
            setCitizenship={setCitizenship}
            setResidence={setResidence}
            setType={setFormType}
            importedSubmissionType={null}
            spouse={spouse}
            setSpouse={setSpouse}
            country={selectedCountry}
            setCountry={setSelectedCountry}
            isLoading={isLoading}
            submitForm={submitForm}
            isSuperVisas={match === null}
            isEducationInUS={isEducationInUS}
            setIsEducationInUS={setIsEducationInUS}
          />
          {isCompleted &&
            (currentQuestion === questionLength && match === null ? (
              <Button
                isLoading={isLoading}
                className="mt-[40px] min-w-[200px] px-4 py-2 text-base text-white"
                onClick={() => {
                  submitForm(null);
                }}
              >
                {t(`${NS.Eligibility}:eligibleProgramCards.submit`)}
              </Button>
            ) : (
              <Button
                className={cn('mt-[40px] min-w-[200px] px-4 py-2 text-base text-white', {
                  hidden: match !== null && currentQuestion === 15,
                })}
                onClick={nextQuestion}
              >
                <span>
                  <span>{t(`${NS.Eligibility}:eligibleProgramCards.next`)}</span>{' '}
                  <span>
                    <NextImg className="inline-block pb-[3px] pl-[6px]" />
                  </span>
                </span>
              </Button>
            ))}
        </div>
        {currentQuestion === 1 && failedGeneralEligibility && <GeneralCheckReminder country={selectedCountry} />}
      </div>
    </>
  );
}

export default Eligibility;
